import { useNotificationSound } from "../../../utilities/audio/notificationSounds";
import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';
import { Conversation, Message, sendMessage } from '../../../services/chatService/ChatService';
import MessageEditor, { MessageEditorRef } from './MessageEditor';
import { ValidateOtpResponseDto } from '../../../interfaces/ExtermalUserDtos/ExternalUsersDtos';
import { toast } from 'react-toastify';
import UserInfo from '../../../interfaces/UserInfo';
import { File as FileIcon, Paperclip, Send, Smile, Type, X } from 'lucide-react';
import { GetHeaders } from '../../../services/videoService/VideoService';
import { MAX_FILE_SIZE_KB, MAX_FILE_SIZE_MB } from '../../../Data/SD';
import { formatFileSize } from '../../../utilities/fileUtils';
import EmojiPicker from '../emojiPicker/EmojiPicker';
import {
    ChatDeliveryStatus,
    PendingChatOutboxItem,
    createClientMessageId,
    createOptimisticTempId,
    deleteOutboxItem,
    getConversationOutbox,
    saveOutboxItem,
    updateOutboxItem,
} from '../../../services/chatService/ChatOutbox';
import { useChatConnection } from '../../../contexts/ChatConnectionContext';
import { clearChatDraft, getChatDraft, saveChatDraft } from '../../../services/chatService/ChatDrafts';

interface ThreadReplyEditorProps {
    rootMessage: Message;
    conversation: Conversation;
    onSendOptimistic: (msg: any) => void;
    onUpdateOptimistic: (messageId: number, patch: Record<string, unknown>) => void;
    externalUserDetails?: ValidateOtpResponseDto | null;
    appearance?: "default" | "meeting-dark";
}

export interface ThreadReplyEditorRef {
    retryMessage: (messageId: number) => void;
    acknowledgeMessage: (messageId: number) => void;
    cancelMessage: (messageId: number) => void;
    beginEdit: (message: Message) => void;
}

const ThreadReplyEditor = forwardRef<ThreadReplyEditorRef, ThreadReplyEditorProps>(({ rootMessage, conversation, onSendOptimistic, onUpdateOptimistic, externalUserDetails, appearance = "default" }, ref) => {
    const isMeetingDark = appearance === "meeting-dark";
    const { currentUser } = useAuth();
    const { connection } = useChatConnection();
    const editorRef = useRef<MessageEditorRef>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [replyText, setReplyText] = useState("");
    const [replyDoc, setReplyDoc] = useState<any>(null);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showFormatting, setShowFormatting] = useState(false);
    const [editingMessage, setEditingMessage] = useState<Message | null>(null);
    const draftBeforeEditRef = useRef<{ plaintext: string; doc: unknown } | null>(null);
    const sendQueueRef = useRef<Promise<void>>(Promise.resolve());
    const pendingSendsRef = useRef(new Map<number, PendingChatOutboxItem>());
    const activeDeliveriesRef = useRef(new Set<string>());
    const scheduledDeliveriesRef = useRef(new Set<string>());
    const optimisticUrlsRef = useRef(new Map<number, string>());
    const playSendSound = useNotificationSound("messageSent");
    const draftSaveTimerRef = useRef<number | null>(null);
    const pendingDraftRef = useRef<{ plaintext: string; doc: unknown } | null>(null);

    const currentMyId = currentUser?.uid || externalUserDetails?.externalUserDetailId;

    useEffect(() => {
        if (!currentMyId) return;
        const draft = getChatDraft(currentMyId, conversation.id, rootMessage.id);
        setReplyText(draft?.plaintext ?? "");
        setReplyDoc(draft?.doc ?? null);
        setIsEditorEmpty(!draft?.plaintext.trim());
        const frame = window.requestAnimationFrame(() => {
            if (draft?.doc) editorRef.current?.setContent(draft.doc);
            else if (draft?.plaintext) editorRef.current?.setContent(draft.plaintext);
            else editorRef.current?.clearContent();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [conversation.id, currentMyId, rootMessage.id]);

    useEffect(() => () => {
        if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
        if (currentMyId && pendingDraftRef.current) {
            saveChatDraft({
                userId: currentMyId,
                conversationId: conversation.id,
                threadRootMessageId: rootMessage.id,
                ...pendingDraftRef.current,
                updatedAt: Date.now(),
            });
        }
        pendingDraftRef.current = null;
    }, [conversation.id, currentMyId, rootMessage.id]);

    useEffect(() => () => {
        if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    }, [filePreviewUrl]);

    const clearSelectedFile = () => {
        if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
        setFilePreviewUrl(null);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const selectFile = (file: File): boolean => {
        if (file.size / 1024 > MAX_FILE_SIZE_KB) {
            toast.warn(`File too large. Maximum file size is ${MAX_FILE_SIZE_MB} MB.`);
            return false;
        }
        if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
        setSelectedFile(file);
        setFilePreviewUrl(URL.createObjectURL(file));
        return true;
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) selectFile(file);
    };

    const handleUpdate = (text: string, doc: any, isEmpty: boolean) => {
        setReplyText(text);
        setReplyDoc(doc);
        setIsEditorEmpty(isEmpty);
        if (!currentMyId || editingMessage) return;
        pendingDraftRef.current = { plaintext: text, doc };
        if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = window.setTimeout(() => {
            saveChatDraft({
                userId: currentMyId,
                conversationId: conversation.id,
                threadRootMessageId: rootMessage.id,
                plaintext: text,
                doc,
                updatedAt: Date.now(),
            });
            pendingDraftRef.current = null;
            draftSaveTimerRef.current = null;
        }, 250);
    };

    const acknowledgePendingMessage = async (messageId: number, pending: PendingChatOutboxItem, accepted: Message) => {
        activeDeliveriesRef.current.delete(pending.clientMessageId);
        pendingSendsRef.current.delete(messageId);
        const optimisticUrl = optimisticUrlsRef.current.get(messageId);
        if (optimisticUrl) URL.revokeObjectURL(optimisticUrl);
        optimisticUrlsRef.current.delete(messageId);
        await deleteOutboxItem(pending.clientMessageId);
        onUpdateOptimistic(messageId, {
            ...accepted,
            id: accepted.id,
            clientMessageId: pending.clientMessageId,
            createDate: new Date(accepted.createDate),
            isOptimistic: false,
            deliveryStatus: "sent" as ChatDeliveryStatus,
        });
    };

    const deliverPendingMessage = async (messageId: number, pending: PendingChatOutboxItem) => {
        if (!pendingSendsRef.current.has(messageId)) return;
        if (activeDeliveriesRef.current.has(pending.clientMessageId)) return;
        activeDeliveriesRef.current.add(pending.clientMessageId);
        let lastError: unknown;
        const maximumAttempts = 3;
        for (let attempt = pending.attemptCount; attempt < maximumAttempts; attempt += 1) {
            const deliveryStatus: ChatDeliveryStatus = attempt === 0 ? "sending" : "retrying";
            pending.attemptCount = attempt + 1;
            pending.status = deliveryStatus;
            void updateOutboxItem(pending.clientMessageId, {
                attemptCount: pending.attemptCount,
                status: deliveryStatus,
                lastError: undefined,
            }).catch(error => console.warn("Unable to persist thread delivery state:", error));
            onUpdateOptimistic(messageId, { deliveryStatus });
            try {
                let token = "";
                if (pending.file && !pending.attachmentUrl) {
                    token = currentUser ? await currentUser.getIdToken() : "";
                    const formData = new FormData();
                    formData.append("file", pending.file);
                    const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/upload-attachment`, {
                        method: "POST",
                        headers: GetHeaders(token, false),
                        body: formData,
                    });
                    if (!uploadResponse.ok) throw new Error(await uploadResponse.text() || "File upload failed.");
                    const uploadResult = await uploadResponse.json();
                    pending.attachmentUrl = uploadResult.documentUrl;
                    pending.attachmentOriginalName = uploadResult.originalName;
                    pending.attachmentContentType = uploadResult.contentType;
                    pending.attachmentSize = uploadResult.size;
                    pending.attachmentScanStatus = uploadResult.scanStatus;
                    await updateOutboxItem(pending.clientMessageId, {
                        attachmentUrl: pending.attachmentUrl,
                        attachmentOriginalName: pending.attachmentOriginalName,
                        attachmentContentType: pending.attachmentContentType,
                        attachmentSize: pending.attachmentSize,
                        attachmentScanStatus: pending.attachmentScanStatus,
                    });
                }

                let accepted: Message;
                if (connection?.state === "Connected") {
                    try {
                        accepted = await connection.invoke<Message>(
                            "SendMessage",
                            pending.conversationId,
                            pending.text,
                            pending.doc,
                            pending.parentMessageId,
                            pending.clientMessageId,
                            pending.attachmentUrl,
                        );
                    } catch (signalRError) {
                        console.warn("SignalR thread send failed; retrying through REST.", signalRError);
                        token ||= currentUser ? await currentUser.getIdToken() : "";
                        accepted = await sendMessage(
                            token,
                            pending.conversationId,
                            pending.text,
                            pending.doc,
                            pending.attachmentUrl,
                            pending.parentMessageId,
                            pending.clientMessageId,
                        );
                    }
                } else {
                    token ||= currentUser ? await currentUser.getIdToken() : "";
                    accepted = await sendMessage(
                        token,
                        pending.conversationId,
                        pending.text,
                        pending.doc,
                        pending.attachmentUrl,
                        pending.parentMessageId,
                        pending.clientMessageId,
                    );
                }
                await acknowledgePendingMessage(messageId, pending, accepted);
                return;
            } catch (error) {
                lastError = error;
                const errorMessage = error instanceof Error ? error.message : "Reply delivery failed.";
                const permissionFailure = /403|forbidden|read-only|archived/i.test(errorMessage);
                if (permissionFailure || attempt >= maximumAttempts - 1) break;
                await new Promise(resolve => window.setTimeout(resolve, 500 * (2 ** attempt)));
            }
        }

        activeDeliveriesRef.current.delete(pending.clientMessageId);
        const errorMessage = lastError instanceof Error ? lastError.message : "Reply delivery failed.";
        pending.status = "failed";
        pending.lastError = errorMessage;
        await updateOutboxItem(pending.clientMessageId, { status: "failed", lastError: errorMessage });
        console.error("Failed to send queued thread reply:", lastError);
        onUpdateOptimistic(messageId, { deliveryStatus: "failed" });
        toast.error("Reply could not be sent. Select Retry to try again.");
    };

    const enqueuePendingMessage = (messageId: number, pending: PendingChatOutboxItem) => {
        if (scheduledDeliveriesRef.current.has(pending.clientMessageId)) return;
        scheduledDeliveriesRef.current.add(pending.clientMessageId);
        sendQueueRef.current = sendQueueRef.current
            .catch(() => undefined)
            .then(() => deliverPendingMessage(messageId, pending))
            .finally(() => scheduledDeliveriesRef.current.delete(pending.clientMessageId));
    };

    useImperativeHandle(ref, () => ({
        retryMessage: (messageId: number) => {
            const pending = pendingSendsRef.current.get(messageId);
            if (!pending) return;
            pending.attemptCount = 0;
            pending.status = "queued";
            pending.lastError = undefined;
            void updateOutboxItem(pending.clientMessageId, { attemptCount: 0, status: "queued", lastError: undefined });
            onUpdateOptimistic(messageId, { deliveryStatus: "queued" });
            enqueuePendingMessage(messageId, pending);
        },
        acknowledgeMessage: (messageId: number) => {
            const pending = pendingSendsRef.current.get(messageId);
            if (!pending) return;
            activeDeliveriesRef.current.delete(pending.clientMessageId);
            pendingSendsRef.current.delete(messageId);
            void deleteOutboxItem(pending.clientMessageId);
        },
        cancelMessage: (messageId: number) => {
            const pending = pendingSendsRef.current.get(messageId);
            if (!pending || activeDeliveriesRef.current.has(pending.clientMessageId)) return;
            pendingSendsRef.current.delete(messageId);
            const optimisticUrl = optimisticUrlsRef.current.get(messageId);
            if (optimisticUrl) URL.revokeObjectURL(optimisticUrl);
            optimisticUrlsRef.current.delete(messageId);
            void deleteOutboxItem(pending.clientMessageId);
        },
        beginEdit: (message: Message) => {
            draftBeforeEditRef.current = { plaintext: replyText, doc: replyDoc };
            setEditingMessage(message);
            const payload = message.messageDoc as any;
            const doc = payload?.doc?.type === "doc"
                ? payload.doc
                : payload?.type === "doc"
                ? payload
                : null;
            setReplyText(message.messagePlaintext || message.messageContent || "");
            setReplyDoc(doc);
            setIsEditorEmpty(false);
            window.requestAnimationFrame(() => editorRef.current?.setContent(doc || message.messageContent || ""));
        },
    }));

    const restoreDraftAfterEdit = () => {
        const draft = draftBeforeEditRef.current;
        draftBeforeEditRef.current = null;
        setEditingMessage(null);
        setReplyText(draft?.plaintext ?? "");
        setReplyDoc(draft?.doc ?? null);
        setIsEditorEmpty(!draft?.plaintext.trim());
        window.requestAnimationFrame(() => {
            if (draft?.doc) editorRef.current?.setContent(draft.doc);
            else if (draft?.plaintext) editorRef.current?.setContent(draft.plaintext);
            else editorRef.current?.clearContent();
        });
    };

    const handleSend = async () => {
        if (isEditorEmpty && !selectedFile) return;

        const textToSend = replyText;
        const docToSend = replyDoc;
        const fileToSend = selectedFile;

        if (editingMessage) {
            const original = editingMessage;
            const trimTrailingEmptyNodes = (doc: any) => {
                if (!doc?.content || !Array.isArray(doc.content)) return doc;
                const content = [...doc.content];
                while (content.length > 0) {
                    const lastNode = content[content.length - 1];
                    if (lastNode?.type !== "paragraph" || lastNode?.content?.length) break;
                    content.pop();
                }
                return { ...doc, content };
            };
            const cleanDoc = replyDoc ? trimTrailingEmptyNodes(replyDoc) : null;
            const optimisticPatch = {
                messageContent: replyText,
                messagePlaintext: replyText,
                messageDoc: cleanDoc ? { v: 1, editor: "tiptap", schema: "pm", doc: cleanDoc } : null,
                isEdited: true,
            };
            onUpdateOptimistic(original.id, optimisticPatch);
            restoreDraftAfterEdit();
            try {
                const token = currentUser ? await currentUser.getIdToken() : "";
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/message/${original.id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ newContent: replyText, doc: cleanDoc }),
                });
                if (!response.ok) throw new Error("Failed to update message.");
            } catch (error) {
                onUpdateOptimistic(original.id, { ...original });
                toast.error(error instanceof Error ? error.message : "Failed to update message.");
            }
            return;
        }

        const optimisticAttachmentUrl = fileToSend ? URL.createObjectURL(fileToSend) : null;
        setReplyText("");
        setReplyDoc(null);
        editorRef.current?.clearContent();
        clearSelectedFile();
        if (currentMyId) {
            if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
            draftSaveTimerRef.current = null;
            pendingDraftRef.current = null;
            clearChatDraft(currentMyId, conversation.id, rootMessage.id);
        }

        const trimTrailingEmptyNodes = (doc: any) => {
            if (!doc || !doc.content || !Array.isArray(doc.content)) return doc;
            const newContent = [...doc.content];
            while (newContent.length > 0) {
                const lastNode = newContent[newContent.length - 1];
                if (lastNode.type === 'paragraph' && (!lastNode.content || lastNode.content.length === 0)) {
                    newContent.pop();
                } else break;
            }
            return { ...doc, content: newContent };
        };

        const cleanDoc = docToSend ? trimTrailingEmptyNodes(docToSend) : null;
        const wrappedDoc = cleanDoc ? { v: 1, editor: "tiptap", schema: "pm", doc: cleanDoc } : null;

        const optimisticAuthor = {
            id: currentMyId || "",
            firstName: currentUser?.displayName?.split(' ')[0] || "Me",
            lastName: currentUser?.displayName?.split(' ')[1] || "",
            displayName: currentUser?.displayName || "Me",
            email: currentUser?.email || "",
            scid: null,
            joinDate: new Date(),
            organization: {} as any,
            roles: [],
            regions: [],
        } as UserInfo;

        const clientMessageId = createClientMessageId();
        const tempId = createOptimisticTempId(clientMessageId);
        if (optimisticAttachmentUrl) optimisticUrlsRef.current.set(tempId, optimisticAttachmentUrl);
        const optimisticMsg = {
            id: tempId,
            clientMessageId,
            conversationId: conversation.id,
            author: optimisticAuthor,
            messageContent: textToSend,
            messageDoc: wrappedDoc,
            createDate: new Date(),
            messageType: "USER",
            isOptimistic: true,
            deliveryStatus: "queued",
            optimisticAttachmentName: fileToSend?.name,
            reactions: [],
            isDeleted: false,
            isEdited: false,
            threadReplyCount: 0, 
            attachmentType: fileToSend?.type.startsWith("image/") ? "image" : fileToSend ? "other" : "",
            attachmentOriginalName: fileToSend?.name,
            attachmentContentType: fileToSend?.type,
            attachmentSize: fileToSend?.size,
            attachmentScanStatus: fileToSend ? "pending" : undefined,
            attachmentUrl: optimisticAttachmentUrl,
            lastReadAt: new Date().getTime(),
            parentMessageId: rootMessage.id,
            threadRootMessageId: rootMessage.id
        };

        const pending: PendingChatOutboxItem = {
            clientMessageId,
            tempId,
            userId: currentMyId || "",
            conversationId: conversation.id,
            parentMessageId: rootMessage.id,
            text: textToSend,
            doc: cleanDoc,
            file: fileToSend || undefined,
            attachmentType: optimisticMsg.attachmentType,
            attachmentOriginalName: fileToSend?.name,
            attachmentContentType: fileToSend?.type,
            attachmentSize: fileToSend?.size,
            attachmentScanStatus: fileToSend ? "pending" : undefined,
            createdAt: Date.now(),
            attemptCount: 0,
            status: "queued",
        };
        pendingSendsRef.current.set(tempId, pending);
        playSendSound();
        onSendOptimistic(optimisticMsg);
        try {
            await saveOutboxItem(pending);
        } catch (error) {
            console.error("Unable to persist the queued thread reply:", error);
            toast.warn("This reply is queued, but it may not survive a page refresh.");
        }
        enqueuePendingMessage(tempId, pending);
    };

    useEffect(() => {
        if (!currentMyId || !conversation.id || !rootMessage.id) return;
        let cancelled = false;

        const restoreThreadOutbox = async () => {
            try {
                const storedItems = (await getConversationOutbox(currentMyId, conversation.id))
                    .filter(item => item.parentMessageId === rootMessage.id);
                if (cancelled) return;

                for (const storedItem of storedItems) {
                    if (storedItem.status === "sent") {
                        await deleteOutboxItem(storedItem.clientMessageId);
                        continue;
                    }
                    if (pendingSendsRef.current.has(storedItem.tempId)) continue;

                    pendingSendsRef.current.set(storedItem.tempId, storedItem);
                    const optimisticAttachmentUrl = storedItem.file ? URL.createObjectURL(storedItem.file) : null;
                    if (optimisticAttachmentUrl) optimisticUrlsRef.current.set(storedItem.tempId, optimisticAttachmentUrl);
                    const optimisticAuthor = {
                        id: currentMyId,
                        firstName: currentUser?.displayName?.split(" ")[0] || "Me",
                        lastName: currentUser?.displayName?.split(" ")[1] || "",
                        displayName: currentUser?.displayName || "Me",
                        email: currentUser?.email || "",
                        scid: null,
                        joinDate: new Date(),
                        organization: {} as any,
                        roles: [],
                        regions: [],
                    } as UserInfo;

                    onSendOptimistic({
                        id: storedItem.tempId,
                        clientMessageId: storedItem.clientMessageId,
                        conversationId: storedItem.conversationId,
                        author: optimisticAuthor,
                        messageContent: storedItem.text,
                        messageDoc: storedItem.doc ? { v: 1, editor: "tiptap", schema: "pm", doc: storedItem.doc } : null,
                        createDate: new Date(storedItem.createdAt),
                        messageType: "USER",
                        isOptimistic: true,
                        deliveryStatus: storedItem.status === "failed" ? "failed" : "queued",
                        optimisticAttachmentName: storedItem.file?.name,
                        reactions: [],
                        isDeleted: false,
                        isEdited: false,
                        threadReplyCount: 0,
                        attachmentType: storedItem.attachmentType || "",
                        attachmentOriginalName: storedItem.attachmentOriginalName ?? storedItem.file?.name,
                        attachmentContentType: storedItem.attachmentContentType ?? storedItem.file?.type,
                        attachmentSize: storedItem.attachmentSize ?? storedItem.file?.size,
                        attachmentScanStatus: storedItem.attachmentScanStatus,
                        attachmentUrl: optimisticAttachmentUrl,
                        lastReadAt: storedItem.createdAt,
                        parentMessageId: rootMessage.id,
                        threadRootMessageId: rootMessage.id,
                    });

                    if (storedItem.status !== "failed") {
                        storedItem.status = "queued";
                        await updateOutboxItem(storedItem.clientMessageId, { status: "queued" });
                        enqueuePendingMessage(storedItem.tempId, storedItem);
                    }
                }
            } catch (error) {
                console.error("Unable to restore queued thread replies:", error);
            }
        };

        void restoreThreadOutbox();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation.id, currentMyId, currentUser, rootMessage.id]);

    return (
        <div className={`chat-composer relative flex flex-col w-full rounded-2xl shadow-sm transition-all h-auto z-10 ${isMeetingDark ? 'bg-slate-900 focus-within:ring-2 focus-within:ring-[#7E57C2]/30' : 'bg-slate-100 focus-within:ring-2 focus-within:ring-[#7E57C2]/25'}`}>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
            {editingMessage && (
                <div className={`flex items-center justify-between gap-3 rounded-t-2xl px-3 py-2 text-xs ${isMeetingDark ? 'bg-purple-400/10 text-purple-200' : 'bg-purple-50 text-[#6847A3]'}`} role="status">
                    <span className="truncate font-semibold">Editing message</span>
                    <button type="button" onClick={restoreDraftAfterEdit} className="rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10" aria-label="Cancel editing"><X size={14} /></button>
                </div>
            )}
            {selectedFile && (
                <div className="chat-selected-file mx-2 mt-2 flex items-center gap-2 rounded-xl p-2">
                    <div className="chat-selected-file-preview flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                        {selectedFile.type.startsWith("image/") && filePreviewUrl
                            ? <img src={filePreviewUrl} alt="Attachment preview" className="h-full w-full object-cover" />
                            : <FileIcon size={17} />}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="m-0 truncate text-xs font-semibold text-slate-700">{selectedFile.name}</p>
                        <p className="m-0 mt-0.5 text-[10px] text-slate-500">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <button type="button" onClick={clearSelectedFile} className="rounded-lg p-1.5 text-slate-400 hover:text-red-500" aria-label="Remove attachment"><X size={14} /></button>
                </div>
            )}
            <MessageEditor ref={editorRef} onUpdate={handleUpdate} onSendMessage={handleSend} onPasteFile={selectFile} users={conversation.participants ?? []} compact showFormatting={showFormatting} canSendWithoutText={Boolean(selectedFile)} appearance={appearance} placeholder="Reply…" />
            <div className={`chat-composer-actions flex items-center justify-between px-3 py-2 shrink-0 rounded-b-2xl ${isMeetingDark ? 'bg-slate-800/80' : 'bg-slate-50/80'}`}>
                <div className="relative flex items-center gap-1">
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={Boolean(editingMessage)} className="chat-composer-option flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 disabled:cursor-not-allowed disabled:opacity-40" aria-label={editingMessage ? "Attachments are unavailable while editing" : "Attach a file"}><Paperclip size={17} /></button>
                    <div className="relative">
                        <button data-emoji-picker-trigger type="button" onClick={() => setShowEmojiPicker(current => !current)} className={`chat-composer-option flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 ${showEmojiPicker ? 'chat-composer-option-active' : ''}`} aria-label="Choose an emoji" aria-expanded={showEmojiPicker}><Smile size={17} /></button>
                        {showEmojiPicker && (
                            <div className="chat-emoji-popover absolute bottom-[calc(100%+8px)] left-0 z-50">
                                <EmojiPicker
                                    autoFocusSearch
                                    onDismiss={() => setShowEmojiPicker(false)}
                                    onSelect={(emoji) => {
                                        editorRef.current?.insertEmoji(emoji);
                                        setShowEmojiPicker(false);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <button type="button" onClick={() => setShowFormatting(current => !current)} className={`chat-composer-option flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 ${showFormatting ? 'chat-composer-option-active' : ''}`} aria-label="Toggle formatting"><Type size={17} /></button>
                </div>
                <button className={`chat-send-button w-9 h-9 flex items-center justify-center rounded-full transition-all shadow-sm ${(isEditorEmpty && !selectedFile) ? (isMeetingDark ? 'chat-send-button-disabled bg-slate-700 text-slate-500 cursor-not-allowed' : 'chat-send-button-disabled bg-slate-200 text-slate-400 cursor-not-allowed') : 'bg-[#7E57C2] hover:bg-[#6F49B5] text-white'}`} onClick={handleSend} disabled={isEditorEmpty && !selectedFile} aria-label={editingMessage ? "Save edited message" : "Send reply"}>
                    <Send size={16} className="-ml-0.5" />
                </button>
            </div>
        </div>
    );
});

export default ThreadReplyEditor;
